import React, { createContext, useState, useEffect, useCallback, type ReactNode } from 'react'

const ADMIN_AUTH_STORAGE_KEY = 'simple_admin_auth'
const ADMIN_SECRET_TOKEN = import.meta.env.VITE_ADMIN_SECRET_TOKEN || 'neeraj-admin-2024'

interface AdminAuthState {
  isAuthenticated: boolean
  isLoading: boolean
  email: string | null
  error: string | null
  expiresAt: number | null
}

interface SimpleAdminAuthContextValue extends AdminAuthState {
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  validateSecretToken: (token: string) => boolean
}

export const SimpleAdminAuthContext = createContext<SimpleAdminAuthContextValue | null>(null)

interface SimpleAdminAuthProviderProps {
  children: ReactNode
}

export function SimpleAdminAuthProvider({ children }: SimpleAdminAuthProviderProps) {
  const [state, setState] = useState<AdminAuthState>({
    isAuthenticated: false,
    isLoading: true,
    email: null,
    error: null,
    expiresAt: null
  })

  // Get the admin-auth function URL based on current Supabase URL
  const getAuthFunctionUrl = useCallback(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
    return supabaseUrl.replace('.supabase.co', '.functions.supabase.co') + '/admin-auth'
  }, [])

  // Verify stored token on mount
  useEffect(() => {
    const verifyStoredToken = async () => {
      const stored = sessionStorage.getItem(ADMIN_AUTH_STORAGE_KEY)

      if (!stored) {
        setState(prev => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const { token, email, expiresAt } = JSON.parse(stored)

        // Check if token is expired client-side first
        if (expiresAt && Date.now() > expiresAt) {
          sessionStorage.removeItem(ADMIN_AUTH_STORAGE_KEY)
          setState(prev => ({ ...prev, isLoading: false }))
          return
        }

        // Verify token with backend
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
        const response = await fetch(`${getAuthFunctionUrl()}?action=verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': anonKey,
            'Authorization': `Bearer ${anonKey}`
          },
          body: JSON.stringify({ token })
        })

        const data = await response.json()

        if (data.valid) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            email: data.email || email,
            error: null,
            expiresAt: data.expiresAt || expiresAt
          })
        } else {
          // Token invalid, clear storage
          sessionStorage.removeItem(ADMIN_AUTH_STORAGE_KEY)
          setState(prev => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        console.error('Token verification failed:', error)
        sessionStorage.removeItem(ADMIN_AUTH_STORAGE_KEY)
        setState(prev => ({ ...prev, isLoading: false }))
      }
    }

    verifyStoredToken()
  }, [getAuthFunctionUrl])

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
      const response = await fetch(`${getAuthFunctionUrl()}?action=login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${anonKey}`
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (!response.ok) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: data.error || 'Login failed'
        }))
        return false
      }

      const expiresAt = Date.now() + data.expiresIn

      // Store token in sessionStorage
      sessionStorage.setItem(ADMIN_AUTH_STORAGE_KEY, JSON.stringify({
        token: data.token,
        email,
        expiresAt
      }))

      setState({
        isAuthenticated: true,
        isLoading: false,
        email,
        error: null,
        expiresAt
      })

      return true
    } catch (error) {
      console.error('Login error:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Network error. Please try again.'
      }))
      return false
    }
  }, [getAuthFunctionUrl])

  const logout = useCallback(() => {
    sessionStorage.removeItem(ADMIN_AUTH_STORAGE_KEY)
    setState({
      isAuthenticated: false,
      isLoading: false,
      email: null,
      error: null,
      expiresAt: null
    })
  }, [])

  const validateSecretToken = useCallback((token: string): boolean => {
    return token === ADMIN_SECRET_TOKEN
  }, [])

  return (
    <SimpleAdminAuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        validateSecretToken
      }}
    >
      {children}
    </SimpleAdminAuthContext.Provider>
  )
}
